import { onUnmounted, ref } from "vue";
import {
  ASYNC_FLOW_MESSAGE_EVENT_NAME,
  AsyncFlowMessageType,
} from "@f3/shared/async-flow-messages";
import { useAppStore } from "@/stores/app.store";
import { startOAuth } from "@/utils/app-navigator";

export interface OAuthFlowOptions {
  authUrl: string;
  onError: (error: string) => void;
  onCancel?: () => void;
  onComplete?: () => void;
}

/**
 * Drives the OAuth dance for non-embed contexts: opens the provider login
 * URL in a popup, waits for the popup to close (signalling either a
 * successful redirect-back or a user cancel), and surfaces the result to
 * the caller.
 *
 * The actual auth code handoff happens via postMessage from
 * `oauth-callback.vue` (which is loaded inside the popup as Google's
 * redirect target). That message is received by `useAuthAsyncFlowManager`
 * — NOT by this composable — because the auth modal owns the listener for
 * the broader auth state machine. This composable only owns the popup
 * lifecycle.
 *
 * Embed mode (Safari + iframe fix):
 *  - When `appStore.isEmbed`, we ask the host SDK to open the OAuth
 *    popup via `app-navigator.startOAuth` → `ff-oauth:ask`. The popup is
 *    a sibling of the host (not the iframe) so `window.opener` survives
 *    Safari's COOP severing and the auth-success postMessage lands. The
 *    SDK then forwards the auth code down to us via `ff-oauth:res`,
 *    which we re-broadcast as a same-origin async-flow message so the
 *    existing `useAuthAsyncFlowManager` listener consumes it unchanged.
 *  - Requires SDK version 1.9.0+ on the host. Older SDKs ignore
 *    `ff-oauth:ask`, the iframe-side ask times out after 5 min, and the
 *    embed user is blocked. There is no automatic fallback because
 *    opening the popup from inside the iframe (the legacy path) is
 *    exactly what's broken on Safari.
 *
 * Standalone mode:
 *  - Popup blocked by browser: falls back to `window.location.href`
 *    (full-page redirect). Embed callers should never reach this branch.
 */
export function useOauthFlowManager() {
  const popup = ref<Window | null>(null);
  const checkClosedInterval = ref<ReturnType<typeof setInterval> | null>(null);
  const timeoutId = ref<ReturnType<typeof setTimeout> | null>(null);

  function openPopup(url: string): Window | null {
    try {
      // Popups must be opened synchronously from a user gesture or Safari
      // / Chrome will block them. Callers must not await any async work
      // between the click handler and this call.
      return window.open(
        url,
        "oauth-popup",
        "width=500,height=600,scrollbars=yes,resizable=yes,status=yes,location=yes",
      );
    } catch (error) {
      console.warn("[OAuthFlowManager] Popup blocked:", error);
      return null;
    }
  }

  function cleanup(): void {
    if (checkClosedInterval.value) {
      clearInterval(checkClosedInterval.value);
      checkClosedInterval.value = null;
    }
    if (timeoutId.value) {
      clearTimeout(timeoutId.value);
      timeoutId.value = null;
    }
    popup.value = null;
  }

  async function startOAuthFlow(options: OAuthFlowOptions): Promise<void> {
    const { authUrl, onError, onCancel, onComplete } = options;
    const appStore = useAppStore();

    // Embed mode: the host SDK opens the popup from its top-level
    // window so window.opener survives Safari COOP, then forwards the
    // auth code back via ff-oauth:res. We re-broadcast the result as a
    // same-origin async-flow message so the existing
    // useAuthAsyncFlowManager listener picks it up unchanged.
    if (appStore.isEmbed) {
      try {
        const result = await startOAuth(authUrl);
        window.postMessage(
          {
            type: ASYNC_FLOW_MESSAGE_EVENT_NAME,
            data: {
              type: AsyncFlowMessageType.ValidateAuthenticationSuccess,
              code: result.code,
              state: result.state,
              redirect_uri: result.redirect_uri,
            },
          },
          window.location.origin,
        );
        onComplete?.();
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        if (message === "cancelled") {
          onCancel?.();
        } else {
          onError(message);
        }
        onComplete?.();
      }
      return;
    }

    try {
      popup.value = openPopup(authUrl);

      if (!popup.value) {
        // Popup blocked. Fallback: full-page redirect. The auth-callback
        // page handles both popup-relay and full-redirect modes.
        console.warn(
          "[OAuthFlowManager] Popup blocked — falling back to full-page redirect. " +
            "If running inside an iframe, the OAuth provider may refuse to render.",
        );
        window.location.href = authUrl;
        return;
      }

      // Poll for the popup closing. Safari blocks `popup.closed` reads
      // from a cross-origin window during the OAuth round-trip, so we
      // wrap in try/catch and treat exceptions as "still open". Once the
      // popup navigates back to our origin (oauth-callback.vue), the
      // property is readable again and we get a `true` reading once the
      // callback page calls `window.close()`.
      checkClosedInterval.value = setInterval(() => {
        let closed = false;
        try {
          closed = popup.value?.closed === true;
        } catch {
          // Cross-origin Document Mode, popup is still alive.
          closed = false;
        }
        if (closed) {
          cleanup();
          // Distinguishing cancel vs success requires cooperation from
          // the auth state machine — `onComplete` runs whether the user
          // succeeded or cancelled. The auth modal's
          // ValidateAuthenticationSuccess listener handles the success
          // case; if no token arrives within its own timeout, the modal
          // surfaces a "sign in cancelled" message. We always call the
          // popup-cancelled hook here so the calling button can re-enable
          // its loading state.
          onCancel?.();
          onComplete?.();
        }
      }, 500);

      // Hard 5-minute ceiling. The provider's own session usually expires
      // earlier than this; the timeout is a safety net for users who
      // wander off mid-flow.
      timeoutId.value = setTimeout(
        () => {
          cleanup();
          try {
            popup.value?.close();
          } catch {
            // ignore — popup may already be gone
          }
          onError("OAuth flow timed out");
          onComplete?.();
        },
        5 * 60 * 1000,
      );
    } catch (error) {
      console.error("[OAuthFlowManager] OAuth flow error:", error);
      onError("Failed to start OAuth flow");
      onComplete?.();
    }
  }

  onUnmounted(() => {
    cleanup();
  });

  return {
    startOAuthFlow,
  };
}
